import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  CircularProgress,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  Collapse,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AutoFixHigh as AutoFixHighIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AllInbox as AllInboxIcon,
  Inventory as InventoryIcon,
  CallSplit as CallSplitIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import StatusBadge from 'src/components/TransferOrder/StatusBadge';
import Loader from 'src/components/Loader';
import { TransferOrder, TransferOrderStatus, AllocationResponse, ManualAllocation } from 'src/types/TransferOrder.types';
import { bulkBarcodeService, BarcodeSearchResult } from '../../services/bulk-barcode-service';
import { TransferOrderService } from 'src/services/transfer-order-service';
import { toast } from 'react-toastify';

interface AllocationRow {
  id: string;
  barcode: string;
  requestedQuantity: number;
  searchResult: BarcodeSearchResult | null;
  selectedSource?: {
    storageUnitId: string;
    storageUnitName: string;
    boxBarcode?: string;
    availableQuantity: number;
  };
  allocatedQuantity: number;
  loading: boolean;
  error?: string;
  expanded: boolean;
  recordId?: string; // Original record ID from API
  isSplit?: boolean; // Whether this is a split allocation
  parentBarcode?: string; // Original barcode if this is a split
}

interface BoxGroup {
  boxBarcode: string;
  items: AllocationRow[];
  totalRequested: number;
  totalAllocated: number;
}

const AllocateOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentOrder, setCurrentOrder] = useState<TransferOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [allocationType, setAllocationType] = useState<'smart' | 'manual'>('smart');
  const [showPreview, setShowPreview] = useState(false);
  const [allocationResult, setAllocationResult] = useState<AllocationResponse | null>(null);
  const [allocationRows, setAllocationRows] = useState<AllocationRow[]>([]);
  const [bulkSearching, setBulkSearching] = useState(false);
  const [expandedBoxes, setExpandedBoxes] = useState<Set<string>>(new Set());
  const [expandedLooseItems, setExpandedLooseItems] = useState(true);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const fetchOrder = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const order = await TransferOrderService.getById(id);
      setCurrentOrder(order);
    } catch (error: any) {
      console.error('Failed to fetch transfer order:', error);
      const errorMessage = error?.message || 'Failed to load transfer order';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  // Auto-search when switching to manual mode
  useEffect(() => {
    if (allocationType === 'manual' && !isAlreadyAllocated && allocationRows.length > 0) {
      // Check if there are items without search results
      const needsSearch = allocationRows.some(row => row.barcode && !row.searchResult && !row.loading);
      if (needsSearch) {
        handleBulkSearch();
      }
    }
  }, [allocationType]);

  // Initialize allocation rows from order items or records
  useEffect(() => {
    if (allocationRows.length > 0) return; // Already initialized

    // Check if we have Records (after allocation) or Items (before allocation)
    if (currentOrder?.Records && currentOrder.Records.length > 0) {
      // Use Records if available (already allocated items)
      const rows: AllocationRow[] = currentOrder.Records.map((record) => ({
        id: record.id,
        recordId: record.id,
        barcode: record.barcode,
        requestedQuantity: record.requestedQuantity,
        searchResult: null,
        selectedSource: record.sourceStorageUnitId ? {
          storageUnitId: record.sourceStorageUnitId,
          storageUnitName: record.sourceStorageUnitName || 'Unknown',
          boxBarcode: record.boxRefNumber || undefined,
          availableQuantity: record.allocatedQuantity,
        } : undefined,
        allocatedQuantity: record.allocatedQuantity,
        loading: false,
        expanded: false,
      }));
      setAllocationRows(rows);
    } else if (currentOrder?.Items && currentOrder.Items.length > 0) {
      // Use Items if Records is not available (new order)
      const rows: AllocationRow[] = currentOrder.Items.map((item, index) => ({
        id: `item-${index}`,
        barcode: item.barcode,
        requestedQuantity: item.requestedQuantity,
        searchResult: null,
        allocatedQuantity: 0,
        loading: false,
        expanded: false,
      }));
      setAllocationRows(rows);
    }
  }, [currentOrder, allocationRows.length]);

  // Group items by box
  const boxGroups = useMemo(() => {
    const groups = new Map<string, BoxGroup>();

    allocationRows.forEach(item => {
      const boxBarcode = item.selectedSource?.boxBarcode;
      if (boxBarcode) {
        if (!groups.has(boxBarcode)) {
          groups.set(boxBarcode, {
            boxBarcode,
            items: [],
            totalRequested: 0,
            totalAllocated: 0,
          });
        }
        const group = groups.get(boxBarcode)!;
        group.items.push(item);
        group.totalRequested += item.requestedQuantity;
        group.totalAllocated += item.allocatedQuantity;
      }
    });

    return Array.from(groups.values());
  }, [allocationRows]);

  // Loose items (not in any box)
  const looseItems = useMemo(() => {
    return allocationRows.filter(item => !item.selectedSource?.boxBarcode);
  }, [allocationRows]);

  const toggleBoxExpansion = (boxBarcode: string) => {
    setExpandedBoxes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(boxBarcode)) {
        newSet.delete(boxBarcode);
      } else {
        newSet.add(boxBarcode);
      }
      return newSet;
    });
  };

  // Helper functions for source management (similar to newMoveOut)
  const normalizeBox = (b?: string | null) => (b === 'independent' ? '' : (b ?? ''));
  const SOURCE_KEY_SEP = '||';

  const makeSourceKey = (storageUnitId: string, boxBarcode?: string) =>
    `${storageUnitId}${SOURCE_KEY_SEP}${boxBarcode ?? ''}`;

  const parseSourceKey = (key: string) => {
    const idx = key.lastIndexOf(SOURCE_KEY_SEP);
    if (idx === -1) return { storageUnitId: '', boxBarcode: '' };
    return {
      storageUnitId: key.slice(0, idx),
      boxBarcode: key.slice(idx + SOURCE_KEY_SEP.length)
    };
  };

  // Split allocation - allows allocating from multiple sources
  const handleSplitAllocation = (rowId: string) => {
    const row = allocationRows.find(r => r.id === rowId);
    if (!row || !row.searchResult) return;

    const newRowId = `${Date.now()}-split`;
    const newRow: AllocationRow = {
      ...row,
      id: newRowId,
      selectedSource: undefined,
      allocatedQuantity: 0,
      isSplit: true,
      parentBarcode: row.barcode,
    };

    setAllocationRows(prev => {
      const index = prev.findIndex(r => r.id === rowId);
      const newRows = [...prev];
      newRows.splice(index + 1, 0, newRow);
      return newRows;
    });

    dispatch(showAlert({
      message: `Split allocation created for ${row.barcode}. You can now select a different source.`,
      type: 'info',
    }));
  };

  // Remove a split allocation row
  const handleRemoveSplit = (rowId: string) => {
    setAllocationRows(prev => prev.filter(r => r.id !== rowId));
  };

  // Bulk source selection
  const handleBulkSourceSelection = (sourceKey: string) => {
    if (!sourceKey || selectedRows.size === 0) return;

    const { storageUnitId, boxBarcode } = parseSourceKey(sourceKey);

    setAllocationRows(prev => prev.map(row => {
      if (!selectedRows.has(row.id) || !row.searchResult) return row;

      const location = row.searchResult.locations?.find(loc =>
        loc.storageUnitId === storageUnitId
      );

      if (!location) return row;

      let selectedSource;
      if (boxBarcode && boxBarcode !== '') {
        const box = location.boxes?.find(b => b.boxBarcode === boxBarcode);
        if (box) {
          selectedSource = {
            storageUnitId,
            storageUnitName: location.storageUnitName,
            boxBarcode,
            availableQuantity: box.quantity
          };
        }
      } else {
        if (location.independentQuantity && location.independentQuantity > 0) {
          selectedSource = {
            storageUnitId,
            storageUnitName: location.storageUnitName,
            boxBarcode: normalizeBox(''),
            availableQuantity: location.independentQuantity
          };
        }
      }

      if (selectedSource) {
        return {
          ...row,
          selectedSource,
          allocatedQuantity: Math.min(row.requestedQuantity, selectedSource.availableQuantity)
        };
      }
      return row;
    }));

    // Clear selection after bulk operation
    setSelectedRows(new Set());

    dispatch(showAlert({
      message: `Bulk source selection applied to ${selectedRows.size} items`,
      type: 'success',
    }));
  };

  // Get available sources for bulk selection
  const getAvailableSourcesForBulk = () => {
    const sources = new Map<string, { label: string; count: number; }>();
    allocationRows.forEach(row => {
      if (!selectedRows.has(row.id) || !row.searchResult) return;

      row.searchResult.locations?.forEach(location => {
        // Independent quantity
        if (location.independentQuantity && location.independentQuantity > 0) {
          const key = makeSourceKey(location.storageUnitId, '');
          const label = `${location.storageUnitName} (Independent)`;
          sources.set(key, {
            label,
            count: (sources.get(key)?.count || 0) + 1
          });
        }

        // Box quantities
        location.boxes?.forEach(box => {
          const key = makeSourceKey(location.storageUnitId, box.boxBarcode);
          const label = `${location.storageUnitName} - Box: ${box.boxBarcode}`;
          sources.set(key, {
            label,
            count: (sources.get(key)?.count || 0) + 1
          });
        });
      });
    });

    return Array.from(sources.entries()).map(([key, value]) => ({
      key,
      label: `${value.label} (${value.count} items)`,
      count: value.count
    }));
  };

  // Select all items without sources
  const handleSelectAllWithoutSources = () => {
    const rowsWithoutSources = allocationRows.filter(row =>
      row.barcode.trim() && row.searchResult && !row.selectedSource
    );
    const newSelection = new Set(selectedRows);
    rowsWithoutSources.forEach(row => newSelection.add(row.id));
    setSelectedRows(newSelection);
  };

  const handleBulkSearch = async () => {
    // Only search for items that don't have search results yet
    const itemsToSearch = allocationRows.filter(row =>
      row.barcode && !row.searchResult && !row.loading
    );

    if (itemsToSearch.length === 0) return;

    setBulkSearching(true);
    const barcodes = itemsToSearch.map(row => row.barcode);

    try {
      const response = await bulkBarcodeService.searchBarcodes(barcodes, {
        includeLocations: true,
        includeBoxes: true,
      });

      console.log('Bulk search response:', response);

      // Process the API response - response is already BulkBarcodeSearchResult[]
      const resultsMap = new Map<string, BarcodeSearchResult>();

      response.forEach((item) => {
        if (item.barcode && item.result && item.found) {
          // Filter locations to only include those with quantity > 0
          const locations = (item.result.locations || []).filter((loc: any) => {
            const hasIndependent = (loc.independentQuantity || 0) > 0;
            const hasBoxes = (loc.boxes || []).length > 0;
            return hasIndependent || hasBoxes;
          });

          if (locations.length > 0) {
            const normalized: BarcodeSearchResult = {
              type: item.result.type === 'box' ? 'box' : 'unit',
              itemName: item.result.itemName || '',
              unitId: item.result.unitId,
              locations: locations,
              unit: item.result.unit,
            };
            resultsMap.set(item.barcode, normalized);
          }
        }
      });

      setAllocationRows(prev => prev.map(row => {
        if (!itemsToSearch.some(item => item.id === row.id)) {
          return row; // Don't update rows we didn't search for
        }

        const result = resultsMap.get(row.barcode);
        return {
          ...row,
          searchResult: result || null,
          error: result ? undefined : 'Not found in inventory',
        };
      }));

      const foundCount = resultsMap.size;
      const notFoundCount = barcodes.length - foundCount;

      if (foundCount > 0) {
        dispatch(showAlert({
          message: notFoundCount > 0
            ? `Found ${foundCount} of ${barcodes.length} items. ${notFoundCount} not found in inventory.`
            : `Found all ${foundCount} items in inventory`,
          type: notFoundCount > 0 ? 'warning' : 'success',
        }));
      } else {
        dispatch(showAlert({
          message: `No items found in inventory (${barcodes.length} items searched)`,
          type: 'error',
        }));
      }
    } catch (error: any) {
      console.error('Bulk search error:', error);
      dispatch(showAlert({
        message: error.message || 'Bulk search failed',
        type: 'error',
      }));
    } finally {
      setBulkSearching(false);
    }
  };

  const handleSmartAllocation = async () => {
    if (!currentOrder) return;

    setOperationLoading(true);
    try {
      const result = await TransferOrderService.allocate({
        transferOrderId: currentOrder.Id,
        useSmartAllocation: true,
      });

      setAllocationResult(result);
      setShowPreview(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Allocation failed';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleManualAllocation = async () => {
    if (!currentOrder) return;

    // Group allocations by barcode to handle split allocations
    const barcodeGroups = new Map<string, AllocationRow[]>();
    allocationRows.forEach(row => {
      const key = row.barcode;
      if (!barcodeGroups.has(key)) {
        barcodeGroups.set(key, []);
      }
      barcodeGroups.get(key)!.push(row);
    });

    // Validate that all items have sufficient allocation
    const insufficientItems: string[] = [];
    barcodeGroups.forEach((rows, barcode) => {
      const totalAllocated = rows.reduce((sum, row) => sum + row.allocatedQuantity, 0);
      const totalRequested = rows[0].requestedQuantity;

      if (totalAllocated < totalRequested) {
        insufficientItems.push(`${barcode} (${totalAllocated}/${totalRequested})`);
      }

      // Check if all rows have sources selected
      const missingSource = rows.some(row => !row.selectedSource && row.allocatedQuantity > 0);
      if (missingSource) {
        insufficientItems.push(`${barcode} (missing source selection)`);
      }
    });

    if (insufficientItems.length > 0) {
      dispatch(showAlert({
        message: `Please complete allocation for: ${insufficientItems.join(', ')}`,
        type: 'error',
      }));
      return;
    }

    setOperationLoading(true);
    try {
      // Build manual allocations - each row with allocation becomes a separate entry
      const manualAllocations: ManualAllocation[] = allocationRows
        .filter(row => row.selectedSource && row.allocatedQuantity > 0)
        .map(row => ({
          recordId: row.recordId || row.id,
          sourceStorageUnitId: row.selectedSource!.storageUnitId,
          quantity: row.allocatedQuantity,
          boxRefNumber: row.selectedSource!.boxBarcode || undefined,
        }));

      const result = await TransferOrderService.allocate({
        transferOrderId: currentOrder.Id,
        useSmartAllocation: false,
        manualAllocations,
      });

      setAllocationResult(result);
      setShowPreview(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Manual allocation failed';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleConfirmAllocation = async () => {
    if (!allocationResult) return;

    dispatch(
      showAlert({
        message: `Successfully allocated ${allocationResult.totalRecordsAllocated} items`,
        type: 'success',
      })
    );
    toast.success(`Successfully allocated ${allocationResult.totalRecordsAllocated} items`);

    // Show warnings if any
    if (allocationResult.warnings && allocationResult.warnings.length > 0) {
      allocationResult.warnings.forEach((warning) => {
        dispatch(showAlert({ message: warning, type: 'warning' }));
        toast.warning(warning);
      });
    }

    setShowPreview(false);

    // Refresh order data
    await fetchOrder();

    // Navigate to detail page
    navigate(`/transfer-orders/${id}`);
  };

  const handleCancelAllocation = () => {
    setShowPreview(false);
    setAllocationResult(null);
  };

  const toggleRowExpansion = (rowId: string) => {
    setAllocationRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, expanded: !row.expanded } : row
    ));
  };

  const handleSourceSelection = (rowId: string, location: any, boxBarcode?: string) => {
    setAllocationRows(prev => prev.map(row => {
      if (row.id === rowId) {
        return {
          ...row,
          selectedSource: {
            storageUnitId: location.storageUnitId,
            storageUnitName: location.storageUnitName,
            boxBarcode: boxBarcode === 'independent' ? '' : boxBarcode,
            availableQuantity: boxBarcode === 'independent'
              ? location.independentQuantity || 0
              : location.boxes.find((b: any) => b.boxBarcode === boxBarcode)?.quantity || 0,
          },
        };
      }
      return row;
    }));
  };

  const summary = useMemo(() => {
    // Group by barcode to handle split allocations
    const barcodeGroups = new Map<string, AllocationRow[]>();
    allocationRows.forEach(row => {
      const key = row.barcode;
      if (!barcodeGroups.has(key)) {
        barcodeGroups.set(key, []);
      }
      barcodeGroups.get(key)!.push(row);
    });

    const totalItems = barcodeGroups.size;
    let itemsWithSources = 0;
    let itemsAllocated = 0;
    let totalQuantity = 0;

    barcodeGroups.forEach((rows) => {
      const hasSource = rows.some(row => row.searchResult);
      const totalAllocated = rows.reduce((sum, row) => sum + row.allocatedQuantity, 0);
      const totalRequested = rows[0].requestedQuantity; // All rows have same requested quantity

      if (hasSource) itemsWithSources++;
      if (totalAllocated >= totalRequested) itemsAllocated++;

      totalQuantity += totalAllocated;
    });

    return { totalItems, itemsWithSources, itemsAllocated, totalQuantity };
  }, [allocationRows]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loader />
      </Box>
    );
  }

  if (!currentOrder) {
    return (
      <Box>
        <Alert severity="error">Transfer order not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/transfer-order')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  const isAlreadyAllocated = currentOrder.Status !== TransferOrderStatus.New;

  return (
    <Box sx={{ p: 2 }}>
      {/* Compact Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(`/transfer-orders/${id}`)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h5" fontWeight={600}>
            {isAlreadyAllocated ? 'View Allocation' : 'Allocate Items'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentOrder.OrderName || `Order #${currentOrder.Id.slice(0, 8)}`} - {currentOrder.ClientName}
          </Typography>
        </Box>
        <StatusBadge status={currentOrder.Status} type="order" size="medium" />
      </Stack>

      {/* Warning for already allocated orders */}
      {isAlreadyAllocated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This order has already been allocated. You are viewing the allocation details in read-only mode.
        </Alert>
      )}

      {/* Summary Cards - Compact */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Total Items</Typography>
              <Typography variant="h6" fontWeight={600}>{summary.totalItems}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Allocated Items</Typography>
              <Typography variant="h6" fontWeight={600}>{summary.itemsAllocated}/{summary.totalItems}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Total Quantity</Typography>
              <Typography variant="h6" fontWeight={600}>{summary.totalQuantity}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Boxes / Loose</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Chip label={`${boxGroups.length}`} size="small" color="secondary" variant="outlined" />
                <Chip label={`${looseItems.length}`} size="small" color="default" variant="outlined" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Allocation Method - Compact */}
      {!isAlreadyAllocated && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={allocationType}
                onChange={(e) => setAllocationType(e.target.value as 'smart' | 'manual')}
              >
                <FormControlLabel
                  value="smart"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Smart Allocation</Typography>}
                />
                <FormControlLabel
                  value="manual"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Manual Selection</Typography>}
                />
              </RadioGroup>
            </FormControl>

            {allocationType === 'manual' && bulkSearching && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Searching inventory...
                </Typography>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      {/* Box Groups */}
      {boxGroups.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AllInboxIcon />
              Boxed Items ({boxGroups.length} boxes)
            </Typography>
            <Stack spacing={1}>
              {boxGroups.map((box) => (
                <Card key={box.boxBarcode} variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <IconButton size="small" onClick={() => toggleBoxExpansion(box.boxBarcode)}>
                        {expandedBoxes.has(box.boxBarcode) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                          📦 {box.boxBarcode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {box.items.length} items • {box.totalAllocated}/{box.totalRequested} units allocated
                        </Typography>
                      </Box>
                      <Chip
                        label={box.totalAllocated === box.totalRequested ? 'Fully Allocated' : 'Partially Allocated'}
                        color={box.totalAllocated === box.totalRequested ? 'success' : 'warning'}
                        size="small"
                      />
                    </Stack>
                    <Collapse in={expandedBoxes.has(box.boxBarcode)}>
                      <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Barcode</TableCell>
                              <TableCell align="center">Requested</TableCell>
                              <TableCell align="center">Allocated</TableCell>
                              <TableCell>Source Location</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {box.items.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                                    {item.barcode}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" fontWeight={600}>
                                    {item.requestedQuantity}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" fontWeight={600} color={item.allocatedQuantity === item.requestedQuantity ? 'success.main' : 'warning.main'}>
                                    {item.allocatedQuantity}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontSize="0.875rem">
                                    {item.selectedSource?.storageUnitName || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {item.allocatedQuantity > 0 ? (
                                    <Chip label="Allocated" size="small" color="success" />
                                  ) : (
                                    <Chip label="Pending" size="small" variant="outlined" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Loose Items */}
      {looseItems.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InventoryIcon />
                Independent Items ({looseItems.length})
              </Typography>
              <IconButton size="small" onClick={() => setExpandedLooseItems(!expandedLooseItems)}>
                {expandedLooseItems ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>

            {/* Bulk Actions Toolbar */}
            {allocationType === 'manual' && !isAlreadyAllocated && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Actions:
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSelectAllWithoutSources}
                        disabled={looseItems.filter(r => r.searchResult && !r.selectedSource).length === 0}
                      >
                        Select Without Sources
                      </Button>
                      {selectedRows.size > 0 && getAvailableSourcesForBulk().length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Bulk Set Source</InputLabel>
                          <Select
                            value=""
                            onChange={(e) => handleBulkSourceSelection(e.target.value)}
                            label="Bulk Set Source"
                          >
                            {getAvailableSourcesForBulk().map(source => (
                              <MenuItem key={source.key} value={source.key}>
                                {source.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>
                    <Chip
                      label={`${selectedRows.size} Selected`}
                      size="small"
                      color={selectedRows.size > 0 ? "primary" : "default"}
                      variant="outlined"
                    />
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Collapse in={expandedLooseItems} timeout={0}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {allocationType === 'manual' && !isAlreadyAllocated && <TableCell width={50} padding="checkbox">☐</TableCell>}
                      <TableCell>Barcode</TableCell>
                      <TableCell align="center">Requested</TableCell>
                      <TableCell align="center">To Allocate</TableCell>
                      {allocationType === 'manual' && !isAlreadyAllocated && (
                        <>
                          <TableCell width={250}>Source Location</TableCell>
                          <TableCell align="center" width={100}>Available</TableCell>
                        </>
                      )}
                      {(allocationType !== 'manual' || isAlreadyAllocated) && (
                        <>
                          <TableCell>Source Location</TableCell>
                          <TableCell align="center">Allocated</TableCell>
                        </>
                      )}
                      <TableCell>Status</TableCell>
                      {allocationType === 'manual' && !isAlreadyAllocated && <TableCell width={100}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {looseItems.map((row) => (
                      <TableRow key={row.id} hover sx={{ bgcolor: row.isSplit ? 'action.hover' : 'inherit' }}>
                        {/* Checkbox */}
                        {allocationType === 'manual' && !isAlreadyAllocated && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRows.has(row.id)}
                              onChange={(e) => {
                                const newSelection = new Set(selectedRows);
                                if (e.target.checked) {
                                  newSelection.add(row.id);
                                } else {
                                  newSelection.delete(row.id);
                                }
                                setSelectedRows(newSelection);
                              }}
                              disabled={!row.searchResult}
                              size="small"
                            />
                          </TableCell>
                        )}

                        {/* Barcode */}
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                            {row.isSplit && '↳ '}
                            {row.barcode}
                          </Typography>
                        </TableCell>

                        {/* Requested Quantity */}
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {row.requestedQuantity}
                          </Typography>
                        </TableCell>

                        {/* Allocated Quantity Input (Manual Mode) or Display (View Mode) */}
                        <TableCell align="center">
                          {allocationType === 'manual' && !isAlreadyAllocated ? (
                            <TextField
                              type="number"
                              value={row.allocatedQuantity}
                              onChange={(e) => {
                                const newQuantity = parseInt(e.target.value) || 0;
                                const maxQuantity = row.selectedSource?.availableQuantity || row.requestedQuantity;
                                setAllocationRows(prev => prev.map(r =>
                                  r.id === row.id ? { ...r, allocatedQuantity: Math.min(Math.max(0, newQuantity), maxQuantity) } : r
                                ));
                              }}
                              size="small"
                              inputProps={{
                                min: 0,
                                max: row.selectedSource?.availableQuantity || row.requestedQuantity,
                                style: { width: 60, fontSize: '0.8rem', textAlign: 'center' }
                              }}
                              sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                            />
                          ) : (
                            <Typography variant="body2" fontWeight={600} color={row.allocatedQuantity === row.requestedQuantity ? 'success.main' : row.allocatedQuantity > 0 ? 'warning.main' : 'inherit'}>
                              {row.allocatedQuantity}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Source Location Dropdown (Manual Mode) or Display (View Mode) */}
                        {allocationType === 'manual' && !isAlreadyAllocated ? (
                          <>
                            <TableCell>
                              {row.searchResult && row.searchResult.locations && row.searchResult.locations.length > 0 ? (
                                <FormControl fullWidth size="small">
                                  <InputLabel sx={{ fontSize: '0.8rem' }}>Source</InputLabel>
                                  <Select
                                    value={row.selectedSource ? makeSourceKey(row.selectedSource.storageUnitId, normalizeBox(row.selectedSource.boxBarcode)) : ''}
                                    onChange={(e) => {
                                      const value = e.target.value as string;
                                      if (!value) return;

                                      const { storageUnitId, boxBarcode } = parseSourceKey(value);
                                      const location = row.searchResult!.locations!.find(l => l.storageUnitId === storageUnitId);
                                      if (!location) return;

                                      let selectedSource;
                                      if (boxBarcode && boxBarcode !== '') {
                                        const box = location.boxes.find(b => b.boxBarcode === boxBarcode);
                                        if (box) {
                                          selectedSource = {
                                            storageUnitId,
                                            storageUnitName: location.storageUnitName,
                                            boxBarcode,
                                            availableQuantity: box.quantity
                                          };
                                        }
                                      } else {
                                        selectedSource = {
                                          storageUnitId,
                                          storageUnitName: location.storageUnitName,
                                          boxBarcode: normalizeBox(''),
                                          availableQuantity: location.independentQuantity || 0
                                        };
                                      }

                                      if (selectedSource) {
                                        setAllocationRows(prev => prev.map(r =>
                                          r.id === row.id ? {
                                            ...r,
                                            selectedSource,
                                            allocatedQuantity: Math.min(r.requestedQuantity, selectedSource.availableQuantity)
                                          } : r
                                        ));
                                      }
                                    }}
                                    label="Source"
                                    sx={{ minWidth: 200, fontSize: '0.8rem' }}
                                  >
                                    {row.searchResult.locations.map((location) => [
                                      ...(location.independentQuantity && location.independentQuantity > 0 ? [{
                                        value: makeSourceKey(location.storageUnitId, ''),
                                        label: `${location.storageUnitName} (Ind: ${location.independentQuantity})`
                                      }] : []),
                                      ...location.boxes.map(box => ({
                                        value: makeSourceKey(location.storageUnitId, box.boxBarcode),
                                        label: `${location.storageUnitName} - ${box.boxBarcode} (${box.quantity})`
                                      }))
                                    ]).flat().map((option) => (
                                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.8rem' }}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {row.selectedSource ? (
                                <Chip
                                  label={row.selectedSource.availableQuantity}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20, minWidth: 40 }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              <Typography variant="body2" fontSize="0.875rem">
                                {row.selectedSource?.storageUnitName || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={600} color={row.allocatedQuantity === row.requestedQuantity ? 'success.main' : row.allocatedQuantity > 0 ? 'warning.main' : 'inherit'}>
                                {row.allocatedQuantity}
                              </Typography>
                            </TableCell>
                          </>
                        )}

                        {/* Status */}
                        <TableCell>
                          {row.loading ? (
                            <CircularProgress size={16} />
                          ) : row.error ? (
                            <Chip label="Not Found" size="small" color="error" />
                          ) : row.allocatedQuantity > 0 ? (
                            <Chip label="Allocated" size="small" color="success" />
                          ) : row.searchResult ? (
                            <Chip label="Available" size="small" color="info" />
                          ) : (
                            <Chip label="Pending" size="small" variant="outlined" />
                          )}
                        </TableCell>

                        {/* Actions */}
                        {allocationType === 'manual' && !isAlreadyAllocated && (
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              {row.searchResult && !row.isSplit && (
                                <Tooltip title="Split Allocation">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSplitAllocation(row.id)}
                                    color="primary"
                                    sx={{ p: 0.5 }}
                                  >
                                    <CallSplitIcon sx={{ fontSize: '1rem' }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {row.isSplit && (
                                <Tooltip title="Remove Split">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveSplit(row.id)}
                                    color="error"
                                    sx={{ p: 0.5 }}
                                  >
                                    <RemoveIcon sx={{ fontSize: '1rem' }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons - Compact */}
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate(`/transfer-orders/${id}`)} size="medium">
          {isAlreadyAllocated ? 'Back to Order Details' : 'Cancel'}
        </Button>
        {!isAlreadyAllocated && (
          <>
            {allocationType === 'smart' ? (
              <Button
                variant="contained"
                startIcon={operationLoading ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                onClick={handleSmartAllocation}
                disabled={operationLoading}
              >
                {operationLoading ? 'Allocating...' : 'Smart Allocate'}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={operationLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={handleManualAllocation}
                disabled={operationLoading || summary.itemsAllocated !== summary.totalItems}
              >
                {operationLoading ? 'Allocating...' : 'Confirm Manual Allocation'}
              </Button>
            )}
          </>
        )}
      </Stack>

      {/* Allocation Preview Dialog */}
      <Dialog open={showPreview} onClose={handleCancelAllocation} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6" fontWeight={600}>
              Allocation Preview
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {allocationResult && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                {allocationResult.message}
              </Alert>

              {allocationResult.warnings && allocationResult.warnings.length > 0 && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Warnings:
                  </Typography>
                  {allocationResult.warnings.map((warning, index) => (
                    <Typography key={index} variant="body2">
                      • {warning}
                    </Typography>
                  ))}
                </Alert>
              )}

              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                Allocation Details:
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Barcode</TableCell>
                      <TableCell align="center">Requested</TableCell>
                      <TableCell align="center">Allocated</TableCell>
                      <TableCell>Source Location</TableCell>
                      <TableCell>Box Ref</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allocationResult.allocations.map((allocation, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {allocation.barcode}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{allocation.requestedQuantity}</TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={
                              allocation.allocatedQuantity === allocation.requestedQuantity
                                ? 'success.main'
                                : 'warning.main'
                            }
                          >
                            {allocation.allocatedQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell>{allocation.sourceStorageUnitName}</TableCell>
                        <TableCell>
                          {allocation.boxRefNumber || (
                            <Typography variant="caption" color="text.secondary">
                              Independent
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Items
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {allocationResult.totalRecordsAllocated}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Quantity
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {allocationResult.totalQuantityAllocated}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAllocation}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmAllocation} color="success">
            Confirm Allocation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllocateOrder;
